export type QuestOutcome = 'completed' | 'lost' | 'cancelled' | 'unknown' | string;

type UnknownRecord = Record<string, unknown>;

const COMPLETED = new Set([
    'victory', 'win', 'won', 'success', 'completed', 'complete', 'done', 'passed',
]);
const LOST = new Set([
    'failed', 'fail', 'lost', 'lose', 'loss', 'timeout', 'timed_out', 'time_out',
    'defeat', 'dead', 'killed',
]);
const CANCELLED = new Set([
    'cancelled', 'canceled', 'cancel', 'aborted', 'abort', 'interrupted',
]);

const asRecord = (value: unknown): UnknownRecord | undefined =>
    value !== null && typeof value === 'object' ? value as UnknownRecord : undefined;

const readNested = (value: unknown, path: string[]): unknown => {
    let current: unknown = value;
    for (const key of path) {
        const record = asRecord(current);
        if (!record) return undefined;
        current = record[key];
    }
    return current;
};

export const readBooleanLike = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value !== 'string') return undefined;

    const normalized = value.trim().toLowerCase();
    if (COMPLETED.has(normalized) || ['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (LOST.has(normalized) || ['false', '0', 'no', 'n'].includes(normalized)) return false;
    return undefined;
};

export const getQuestStatus = (value: unknown): QuestOutcome => {
    const item = asRecord(value);
    if (!item) return 'unknown';

    const rawStatus = item.Status ?? item.status ?? item.QuestStatus ?? item.quest_status ??
        item.Outcome ?? item.outcome;
    if (rawStatus !== undefined && rawStatus !== null && String(rawStatus).trim()) {
        const status = String(rawStatus).trim().toLowerCase();
        if (COMPLETED.has(status)) return 'completed';
        if (LOST.has(status)) return 'lost';
        if (CANCELLED.has(status)) return 'cancelled';
        return status;
    }

    const candidates = [
        item.Success,
        item.success,
        item.Victory,
        item.victory,
        item.Completed,
        item.completed,
        readNested(item, ['Progress', 'success']),
        readNested(item, ['progress', 'success']),
        readNested(item, ['Telemetry', 'success']),
        readNested(item, ['telemetry', 'success']),
        readNested(item, ['Telemetry', 'gameSpecific', 'success']),
        readNested(item, ['telemetry', 'gameSpecific', 'success']),
    ];

    for (const candidate of candidates) {
        const parsed = readBooleanLike(candidate);
        if (parsed !== undefined) return parsed ? 'completed' : 'lost';
    }
    return 'unknown';
};

export const isLostQuest = (value: unknown) => getQuestStatus(value) === 'lost';
export const isCancelledQuest = (value: unknown) => getQuestStatus(value) === 'cancelled';

export const getQuestOutcomeLabel = (value: unknown, language: 'ru' | 'en' = 'ru'): string => {
    if (isLostQuest(value)) return language === 'en' ? 'Defeat' : 'Поражение';
    if (isCancelledQuest(value)) return language === 'en' ? 'Cancelled' : 'Отменено';
    return language === 'en' ? 'Victory' : 'Победа';
};
